import React from 'react';
import PropTypes from 'prop-types';

import { IconButton, Toolbar, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import { Icon } from '@hydra/common/components';

const useStyles = makeStyles(theme => ({
  paginationButton: {
    width: theme.spacing(6),
    height: theme.spacing(6),
    '&.Mui-disabled': {
      '& svg': {
        fill: theme.palette.grey[200]
      }
    },
    '&:hover': {
      background: 'none'
    }
  }
}));

const LinkClick = (index, step, clickHandler) => (ev) => {
  ev.preventDefault();
  clickHandler && clickHandler({ step, index, offset: (index * step) });
};

Pagination.propTypes = {
  currentPage: PropTypes.number,
  step: PropTypes.number,
  totalItems: PropTypes.number.isRequired,
  onPagination: PropTypes.func
};

const LINKS_AROUND_CURRENT = 2;
const MAX_PAGES_VISIBLE = 5;

export const getPageCount = (total, step) => {
  if (total === 0 || step === 0) {
    return 0;
  }

  return Math.floor(total / step) + Math.min(total % step, 1);
};

export const getFirstToShow = (current, pageCount) => {
  return Math.min(
    Math.max(0, current - LINKS_AROUND_CURRENT),
    Math.max(0, pageCount - MAX_PAGES_VISIBLE)
  );
};

export const getShowFirstPageLink = (first, pageCount) => {
  return first > 0 && pageCount > MAX_PAGES_VISIBLE;
};

export const getShowLastPageLink = (first, pageCount) => {
  return first < (pageCount - MAX_PAGES_VISIBLE) && pageCount > MAX_PAGES_VISIBLE;
};

function PaginationButton ({ children, ...props }) {
  const classes = useStyles();
  return (
    <IconButton
      component='span'
      className={classes.paginationButton}
      {...props}
    >
      {children}
    </IconButton>
  );
}

export function Pagination ({ currentPage = 0, step = 10, ...props }) {
  const {
    onPagination,
    totalItems
  } = props;

  const PageLinks = ({ amount, first, currentPage, step, callback }) => {
    const links = [];
    for (let i = first; i < (amount + first); i++) {
      links.push(
        <PaginationButton
          key={i}
          onClick={LinkClick(i, step, callback)}
          color={currentPage === i ? 'primary' : 'default'}
        >
          <Typography>
            {i + 1}
          </Typography>
        </PaginationButton>
      );
    }
    return links;
  };

  const pageCount = getPageCount(totalItems, step);

  const amountOfLinks = Math.min(pageCount, MAX_PAGES_VISIBLE);
  const firstToShow = getFirstToShow(currentPage, pageCount);

  const previousLink = Math.max(0, currentPage - 1);
  const nextLink = Math.min(pageCount - 1, currentPage + 1);

  const showFirstPageLink = getShowFirstPageLink(firstToShow, pageCount);
  const showLastPageLink = getShowLastPageLink(firstToShow, pageCount);

  return (
    <Toolbar variant='outlined' className='pagination'>
      {
        showFirstPageLink
          ? (
            <PaginationButton onClick={LinkClick(0, step, onPagination)}>
              <Typography>1</Typography>
            </PaginationButton>
            )
          : null
      }
      {
        showFirstPageLink
          ? <PaginationButton disabled>...</PaginationButton>
          : null
      }
      {
        PageLinks({
          amount: amountOfLinks,
          callback: onPagination,
          currentPage: currentPage,
          first: firstToShow,
          step: step
        })
      }
      {
        showLastPageLink
          ? <PaginationButton disabled>...</PaginationButton>
          : null
      }
      {
        showLastPageLink
          ? <PaginationButton onClick={LinkClick(pageCount - 1, step, onPagination)}><Typography>{pageCount}</Typography></PaginationButton>
          : null
      }
      <PaginationButton
        disabled={currentPage === 0}
        onClick={LinkClick(previousLink, step, onPagination)}
      >
        <Icon type='chevron-left' />
      </PaginationButton>
      <PaginationButton
        disabled={currentPage === (pageCount - 1)}
        onClick={LinkClick(nextLink, step, onPagination)}
      >
        <Icon type='chevron-right' />
      </PaginationButton>
    </Toolbar>
  );
}

export default Pagination;
